import { useMemo } from 'react';
import useSWR from 'swr';
import {
  fetchPassportMemberStatus,
  type MemberOrganization,
} from '@/utils/approval/passportMemberStatus';

export type PassportMemberStatusSets = {
  deactivatedIds: Set<string>;
  onLeaveIds: Set<string>;
  organizations: Map<string, MemberOrganization | null>;
};

/**
 * passportId 목록의 퇴사·휴직 여부를 한 번에 판정하는 단일 SWR 지점.
 *
 * 두 판정은 같은 passport 상세 응답에서 나오므로 SWR 키도 하나로 둔다 — 키를 나누면
 * 오류가 났을 때 두 키가 각각 재시도해 사람당 요청이 배로 는다. 퇴사만 필요한 곳은
 * useDeactivatedUsers, 휴직만 필요한 곳은 useOnLeaveUsers 로 이 훅을 감싸 쓴다.
 */
const usePassportMemberStatuses = (passportIds: string[]) => {
  // id 목록을 정렬·중복 제거해서 SWR key 로 쓴다 — 순서만 바뀌거나
  // 같은 사람이 중복돼도 같은 key 라 재조회하지 않는다.
  const key = useMemo(() => {
    const uniq = Array.from(new Set(passportIds)).sort();
    return uniq.length > 0 ? ['approval-member-statuses', ...uniq] : null;
  }, [passportIds]);

  const { data, isLoading, error } = useSWR(
    key,
    async ([, ...ids]: string[]): Promise<PassportMemberStatusSets> => {
      const results = await Promise.all(ids.map(fetchPassportMemberStatus));
      return {
        deactivatedIds: new Set(ids.filter((_, i) => results[i].deactivated)),
        onLeaveIds: new Set(ids.filter((_, i) => results[i].onLeave)),
        organizations: new Map(
          ids.map((id, i) => [id, results[i].organization]),
        ),
      };
    },
    { revalidateOnFocus: false },
  );

  return {
    data,
    /**
     * 판정 근거가 도착했는지. 아직이면 '퇴사 아님'으로 단정하면 안 된다.
     * isLoading 은 SWR 이 요청을 effect 에서 시작해 키가 처음 생기는 렌더에서
     * false 로 관측될 수 있어, 빈 셋과 '아직 모름' 을 구분하려면 이 값을 봐야 한다.
     * 조회할 id 가 없으면(key null) 기다릴 것도 없으므로 도착한 것으로 본다.
     */
    isResolved: key === null || data !== undefined || error !== undefined,
    isLoading,
    error,
  };
};

export default usePassportMemberStatuses;
